"use client";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";

type Category = {
  code: string;
  name: string;
};

type Props = {
  categories: Category[];
};

const MobileCategoryBar = ({ categories }: Props) => {
  const router = useRouter();
  const searchParams = useSearchParams() || new URLSearchParams();
  const pathname = usePathname();

  const [topOffset, setTopOffset] = useState(0);

  useEffect(() => {
    const header = document.querySelector("header");
    if (!header) return;

    const updateOffset = () =>
      setTopOffset(header.getBoundingClientRect().height);

    updateOffset();

    const observer = new ResizeObserver(updateOffset);
    observer.observe(header);
    window.addEventListener("resize", updateOffset);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
    };
  }, []);

  if (!categories.length) return null;

  const selectedCodes = (searchParams.get("category") ?? "")
    .split(",")
    .filter(Boolean);

  const toggle = (code: string) => {
    const params = new URLSearchParams(searchParams.toString());
    const isSelected = selectedCodes.includes(code);

    if (isSelected) {
      const next = selectedCodes.filter((c) => c !== code);
      if (next.length) params.set("category", next.join(","));
      else params.delete("category");
    } else {
      params.set("category", [...selectedCodes, code].join(","));
    }

    params.set("page", "1");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    );
  };

  const clearAll = () => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("category");
    params.set("page", "1");
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    requestAnimationFrame(() =>
      window.scrollTo({ top: 0, behavior: "smooth" })
    );
  };

  const isAllActive = selectedCodes.length === 0;

  return (
    <div
      className="sticky z-40 xl:hidden bg-white shadow-sm"
      style={{ top: topOffset }}
    >
      <div className="flex gap-2 overflow-x-auto no-scrollbar px-4 py-3">
        <button
          onClick={clearAll}
          className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ease-out duration-200 ${
            isAllActive
              ? "bg-blue text-white"
              : "bg-white border border-gray-3 text-gray-700 hover:border-blue hover:text-blue"
          }`}
        >
          Wszystko
        </button>

        {categories.map((cat) => {
          const isActive = selectedCodes.includes(cat.code);
          return (
            <button
              key={cat.code}
              onClick={() => toggle(cat.code)}
              className={`rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ease-out duration-200 ${
                isActive
                  ? "bg-blue text-white"
                  : "bg-white border border-gray-3 text-gray-700 hover:border-blue hover:text-blue"
              }`}
            >
              {cat.name}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MobileCategoryBar;
